import { type FormEvent, useEffect, useRef, useState } from 'react'
import { ChevronRight, Search, X } from 'lucide-react'
import { createAppointment } from '../../api/appointments'
import { getClient, listClients } from '../../api/clients'
import { listPatients } from '../../api/patients'
import { getMyPractices, listStaff } from '../../api/staff'
import { Button } from '../../components/ui/Button'
import { ErrorBox, SectionTitle } from '../../components/ui/Card'
import { FormField, inputClass, selectClass } from '../../components/ui/FormField'
import { NumberStepper } from '../../components/ui/NumberStepper'
import { useToast } from '../../components/ui/Toast'
import { cn } from '@/lib/utils'
import type { AppointmentType } from '../../types/appointments'
import { TYPE_LABELS } from '../../types/appointments'
import type { ClientListItem } from '../../types/clients'
import type { MyPractice, StaffMember } from '../../types/clinical'
import type { PatientListItem } from '../../types/patients'

interface Props {
  onClose: () => void
  onCreated: () => void
  initialDate: string   // 'YYYY-MM-DD'
  initialTime: string   // 'HH:MM'
  initialDuration: number
  title?: string
  /** When set, the client & patient are pre-filled and locked — no search/reselect UI. */
  lockedClientId?: number
  lockedPatientId?: number
  /** Pre-selects (but does not lock) the practice dropdown. */
  lockedPracticeId?: number
  defaultAppointmentType?: AppointmentType
}

type FieldErrors = Partial<Record<'practiceId' | 'client' | 'patient' | 'date' | 'time' | 'duration', string>>

export default function AppointmentBookingModal({
  onClose, onCreated, initialDate, initialTime, initialDuration,
  title = 'New Appointment', lockedClientId, lockedPatientId, lockedPracticeId, defaultAppointmentType,
}: Props) {
  const toast = useToast()
  const isLocked = lockedClientId != null

  // ── Client search ────────────────────────────────────────────────────────────
  const [clientQuery, setClientQuery] = useState('')
  const [clientResults, setClientResults] = useState<ClientListItem[]>([])
  const [clientDropdownOpen, setClientDropdownOpen] = useState(false)
  const [clientSearchLoading, setClientSearchLoading] = useState(false)
  const [selectedClient, setSelectedClient] = useState<ClientListItem | null>(null)
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const searchContainerRef = useRef<HTMLDivElement>(null)

  // ── Patient ──────────────────────────────────────────────────────────────────
  const [patients, setPatients] = useState<PatientListItem[]>([])
  const [patientsLoading, setPatientsLoading] = useState(false)
  const [selectedPatientId, setSelectedPatientId] = useState('')

  // ── Schedule ─────────────────────────────────────────────────────────────────
  const [practiceId, setPracticeId] = useState('')
  const [date, setDate] = useState(initialDate)
  const [time, setTime] = useState(initialTime)
  const [duration, setDuration] = useState(String(initialDuration))
  const [vetId, setVetId] = useState('')
  const [appointmentType, setAppointmentType] = useState<AppointmentType>(defaultAppointmentType ?? 'consultation')
  const [reason, setReason] = useState('')
  const [notes, setNotes] = useState('')

  // ── Data ─────────────────────────────────────────────────────────────────────
  const [practices, setPractices] = useState<MyPractice[]>([])
  const [staff, setStaff] = useState<StaffMember[]>([])
  const [initLoading, setInitLoading] = useState(true)
  const [lockedLoading, setLockedLoading] = useState(isLocked)

  // ── UI ───────────────────────────────────────────────────────────────────────
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})

  useEffect(() => {
    getMyPractices()
      .then(p => {
        setPractices(p)
        if (lockedPracticeId != null && p.some(pr => pr.id === lockedPracticeId)) {
          setPracticeId(String(lockedPracticeId))
        } else if (p.length === 1) {
          setPracticeId(String(p[0].id))
        }
      })
      .catch(() => {})
      .finally(() => setInitLoading(false))
  }, [lockedPracticeId])

  // Pre-fill and lock the client when opened in locked mode (e.g. scheduling a follow-up)
  useEffect(() => {
    if (lockedClientId == null) return
    setLockedLoading(true)
    getClient(lockedClientId)
      .then(c => setSelectedClient(c))
      .catch(() => setError('Failed to load client details.'))
      .finally(() => setLockedLoading(false))
  }, [lockedClientId])

  useEffect(() => {
    if (!practiceId) { setStaff([]); return }
    listStaff(Number(practiceId)).then(setStaff).catch(() => setStaff([]))
  }, [practiceId])

  // Debounced client search
  useEffect(() => {
    const q = clientQuery.trim()
    if (!q) { setClientResults([]); setClientDropdownOpen(false); return }
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
    searchTimerRef.current = setTimeout(() => {
      setClientSearchLoading(true)
      listClients({ search: q, limit: 8 })
        .then(res => { setClientResults(res.items); setClientDropdownOpen(res.items.length > 0) })
        .catch(() => {})
        .finally(() => setClientSearchLoading(false))
    }, 280)
    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current) }
  }, [clientQuery])

  // Close dropdown on outside click
  useEffect(() => {
    const handleOutside = (e: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target as Node)) {
        setClientDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleOutside)
    return () => document.removeEventListener('mousedown', handleOutside)
  }, [])

  // Load patients when client selected
  useEffect(() => {
    if (!selectedClient) { setPatients([]); setSelectedPatientId(''); return }
    setPatientsLoading(true)
    listPatients({ client_id: selectedClient.id, limit: 50 })
      .then(res => {
        const active = res.items.filter(p => p.is_active)
        setPatients(active)
        if (lockedPatientId != null) {
          setSelectedPatientId(String(lockedPatientId))
        } else if (active.length === 1) {
          setSelectedPatientId(String(active[0].id))
        }
      })
      .catch(() => {})
      .finally(() => setPatientsLoading(false))
  }, [selectedClient, lockedPatientId])

  const lockedPatient = isLocked ? patients.find(p => String(p.id) === selectedPatientId) : undefined

  const selectClient = (c: ClientListItem) => {
    setSelectedClient(c)
    setClientQuery('')
    setClientDropdownOpen(false)
    setSelectedPatientId('')
    setFieldErrors(prev => ({ ...prev, client: undefined }))
  }

  const clearClient = () => {
    setSelectedClient(null)
    setClientQuery('')
    setPatients([])
    setSelectedPatientId('')
  }

  const validate = (): boolean => {
    const errs: FieldErrors = {}
    if (!practiceId) errs.practiceId = 'Please select a practice.'
    if (!selectedClient) errs.client = 'Please search and select a client.'
    if (!selectedPatientId) errs.patient = 'Please select a patient.'
    if (!date) errs.date = 'Date is required.'
    if (!time) errs.time = 'Time is required.'
    const dur = Number(duration)
    if (!duration || isNaN(dur) || dur < 5 || dur > 480) errs.duration = 'Must be 5–480 minutes.'
    setFieldErrors(errs)
    return Object.keys(errs).length === 0
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (submitting || !validate() || !selectedClient) return
    setSubmitting(true)
    setError(null)
    try {
      await createAppointment({
        practice_id: Number(practiceId),
        patient_id: Number(selectedPatientId),
        client_id: selectedClient.id,
        veterinarian_id: vetId ? Number(vetId) : null,
        scheduled_at: new Date(`${date}T${time}:00`).toISOString(),
        duration_minutes: Number(duration),
        appointment_type: appointmentType,
        reason: reason.trim() || null,
        notes: notes.trim() || null,
      })
      toast.success('Appointment scheduled.')
      onCreated()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.')
      setSubmitting(false)
    }
  }

  if (initLoading) return null

  const activeStaff = staff.filter(s => s.status === 'active')

  return (
    <div
      className="fixed inset-0 z-[10000] bg-[rgba(10,25,22,0.52)] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="bg-surface rounded-[14px] w-full max-w-[520px] shadow-[0_12px_48px_rgba(0,0,0,0.24)] max-h-[92vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-[1.1rem] border-b border-border shrink-0">
          <h2 className="m-0 text-[1.05rem] font-bold text-text-heading">
            {title}
          </h2>
          <button
            onClick={onClose}
            className="bg-transparent border-0 cursor-pointer text-text-3 p-1 rounded-[6px] flex items-center hover:bg-surface-hover transition-colors"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto px-6 py-5 flex-1">
          {error && <ErrorBox className="mb-4">{error}</ErrorBox>}

          <form id="booking-form" onSubmit={(e) => void handleSubmit(e)} noValidate>

            <SectionTitle>Practice</SectionTitle>

            <FormField label="Practice" required error={fieldErrors.practiceId}>
              <select
                value={practiceId}
                onChange={e => { setPracticeId(e.target.value); setFieldErrors(prev => ({ ...prev, practiceId: undefined })) }}
                className={selectClass(!!fieldErrors.practiceId)}
              >
                <option value="">Select practice…</option>
                {practices.map(p => <option key={p.id} value={String(p.id)}>{p.name}</option>)}
              </select>
            </FormField>

            <SectionTitle className="mt-5">Client &amp; Patient</SectionTitle>

            <FormField label="Client" required error={fieldErrors.client}>
              {isLocked ? (
                lockedLoading ? (
                  <div className="text-text-3 text-sm py-2">Loading client…</div>
                ) : selectedClient ? (
                  <div className="px-3 py-[0.6rem] border border-border-input rounded-[6px] bg-surface-alt">
                    <span className="font-semibold text-text">
                      {selectedClient.first_name} {selectedClient.last_name}
                    </span>
                    <span className="text-text-3 text-[0.8rem] ml-2">
                      {selectedClient.email}
                    </span>
                  </div>
                ) : (
                  <div className="text-danger-text text-sm py-2">Could not load client.</div>
                )
              ) : selectedClient ? (
                <div className="flex items-center justify-between px-3 py-[0.6rem] border border-border-input rounded-[6px] bg-surface-alt">
                  <div>
                    <span className="font-semibold text-text">
                      {selectedClient.first_name} {selectedClient.last_name}
                    </span>
                    <span className="text-text-3 text-[0.8rem] ml-2">
                      {selectedClient.email}
                    </span>
                  </div>
                  <button
                    type="button" onClick={clearClient}
                    className="bg-transparent border-0 cursor-pointer text-accent text-[0.8rem] font-semibold px-[6px] py-[2px]"
                  >
                    Change
                  </button>
                </div>
              ) : (
                <div ref={searchContainerRef} className="relative">
                  <div className="relative">
                    <Search size={14} className="absolute left-[10px] top-1/2 -translate-y-1/2 text-text-3 pointer-events-none" />
                    <input
                      type="text"
                      value={clientQuery}
                      onChange={e => setClientQuery(e.target.value)}
                      onFocus={() => clientResults.length > 0 && setClientDropdownOpen(true)}
                      className={cn(inputClass(!!fieldErrors.client), 'pl-8')}
                      placeholder="Search by name, email or phone…"
                      autoComplete="off"
                    />
                  </div>
                  {clientSearchLoading && (
                    <div className="text-[0.78rem] text-text-3 mt-1 pl-[2px]">Searching…</div>
                  )}
                  {clientDropdownOpen && clientResults.length > 0 && (
                    <div className="absolute top-[calc(100%+4px)] left-0 right-0 z-[200] bg-surface border border-border rounded-lg shadow-[0_6px_24px_rgba(0,0,0,0.12)] overflow-hidden">
                      {clientResults.map((c, i) => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => selectClient(c)}
                          className={cn(
                            'flex items-center gap-3 w-full text-left px-[0.9rem] py-[0.7rem] bg-transparent border-0 cursor-pointer text-text hover:bg-surface-hover',
                            i > 0 && 'border-t border-border',
                          )}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-sm">{c.first_name} {c.last_name}</div>
                            <div className="text-[0.78rem] text-text-3 overflow-hidden text-ellipsis whitespace-nowrap">
                              {c.email} · {c.patient_count} patient{c.patient_count !== 1 ? 's' : ''}
                            </div>
                          </div>
                          <ChevronRight size={14} className="text-text-4 shrink-0" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </FormField>

            {selectedClient && (
              <FormField label="Patient" required error={fieldErrors.patient}>
                {isLocked ? (
                  patientsLoading ? (
                    <div className="text-text-3 text-sm py-2">Loading patient…</div>
                  ) : lockedPatient ? (
                    <div className="px-3 py-[0.6rem] border border-border-input rounded-[6px] bg-surface-alt font-semibold text-text">
                      {lockedPatient.name} — {lockedPatient.species}{lockedPatient.breed ? ` (${lockedPatient.breed})` : ''}
                    </div>
                  ) : (
                    <div className="text-danger-text text-sm py-2">Could not load patient.</div>
                  )
                ) : patientsLoading ? (
                  <div className="text-text-3 text-sm py-2">Loading patients…</div>
                ) : patients.length === 0 ? (
                  <div className="text-text-3 text-sm py-2">
                    No active patients for this client.
                  </div>
                ) : (
                  <select
                    value={selectedPatientId}
                    onChange={e => { setSelectedPatientId(e.target.value); setFieldErrors(prev => ({ ...prev, patient: undefined })) }}
                    className={selectClass(!!fieldErrors.patient)}
                  >
                    <option value="">Select patient…</option>
                    {patients.map(p => (
                      <option key={p.id} value={String(p.id)}>
                        {p.name} — {p.species}{p.breed ? ` (${p.breed})` : ''}
                      </option>
                    ))}
                  </select>
                )}
              </FormField>
            )}

            <SectionTitle className="mt-5">Schedule</SectionTitle>

            <div className="grid grid-cols-2 gap-x-4 max-sm:grid-cols-1">
              <FormField label="Date" required error={fieldErrors.date}>
                <input
                  type="date" value={date}
                  onChange={e => { setDate(e.target.value); setFieldErrors(prev => ({ ...prev, date: undefined })) }}
                  className={inputClass(!!fieldErrors.date)}
                />
              </FormField>
              <FormField label="Time" required error={fieldErrors.time}>
                <input
                  type="time" value={time}
                  onChange={e => { setTime(e.target.value); setFieldErrors(prev => ({ ...prev, time: undefined })) }}
                  className={inputClass(!!fieldErrors.time)}
                />
              </FormField>
            </div>

            <div className="grid grid-cols-2 gap-x-4 max-sm:grid-cols-1">
              <FormField label="Duration (min)" required error={fieldErrors.duration}>
                <NumberStepper
                  value={duration}
                  onChange={v => { setDuration(v); setFieldErrors(prev => ({ ...prev, duration: undefined })) }}
                  hasError={!!fieldErrors.duration}
                  min={5} max={480} step={5}
                />
              </FormField>
              <FormField label="Veterinarian" hint="Optional">
                <select
                  value={vetId} onChange={e => setVetId(e.target.value)}
                  className={selectClass()} disabled={!practiceId}
                >
                  <option value="">Unassigned</option>
                  {activeStaff.map(s => (
                    <option key={s.user_id} value={String(s.user_id)}>
                      {s.user_name ?? s.user_email}
                    </option>
                  ))}
                </select>
              </FormField>
            </div>

            <SectionTitle className="mt-5">Details</SectionTitle>

            <FormField label="Appointment type" required>
              <select
                value={appointmentType}
                onChange={e => setAppointmentType(e.target.value as AppointmentType)}
                className={selectClass()}
              >
                {(Object.keys(TYPE_LABELS) as AppointmentType[]).map(t => (
                  <option key={t} value={t}>{TYPE_LABELS[t]}</option>
                ))}
              </select>
            </FormField>

            <FormField label="Reason for visit" hint="Optional">
              <input
                type="text" value={reason} onChange={e => setReason(e.target.value)}
                className={inputClass()} placeholder="Annual check-up, limping, etc." maxLength={500}
              />
            </FormField>

            <FormField label="Notes" hint="Optional">
              <textarea
                value={notes} onChange={e => setNotes(e.target.value)}
                className={cn(inputClass(), 'resize-y min-h-[70px] font-[inherit]')}
                placeholder="Additional notes…"
              />
            </FormField>

          </form>
        </div>

        {/* Footer */}
        <div className="flex gap-3 px-6 py-4 border-t border-border shrink-0">
          <Button type="submit" form="booking-form" disabled={submitting || lockedLoading}>
            {submitting ? 'Scheduling…' : 'Schedule Appointment'}
          </Button>
          <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  )
}
